import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Card from '@/components/Card/Card';
import Checkbox from '@/components/Checkbox/Checkbox';
import Dropdown from '@/components/Dropdown/Dropdown';
import Icon from '@/components/Icons/Icon';
import LoadingSpinner from '@/components/Button/LoadingSpinner/LoadingSpinner';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import {
  EditDrawerSuccessAndFailure,
  EntityToUpdate,
} from '@/pages/Franchisee/SharedComponents/EditDrawerSuccessAndFailure';
import { useConfigureEquityStoreTesting, useEquityStoreBundleSettings } from '@/services/bundles/bundles.service';
import { EquityStoreGroup, EquityStoreTestingResult } from '@/types/nationalProgram.types';
import { Switch } from '@mui/material';
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './EquityStoreTesting.module.css';

const STEP_COUNT = 3;
const OPT_OUT = 'optOut';

const orgLabel = (group: EquityStoreGroup) => group.orgCode || group.orgId;

const EquityStoreTesting: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bundleId, nationalProgramId } = useParams();
  const [step, setStep] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [chosenPrice, setChosenPrice] = useState<string>('');
  const [redeemableOnline, setRedeemableOnline] = useState(false);
  const [visibleOnline, setVisibleOnline] = useState(false);
  const [result, setResult] = useState<EquityStoreTestingResult | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError } = useEquityStoreBundleSettings(
    nationalProgramId !== undefined,
    nationalProgramId ?? '',
  );
  const { mutate, isLoading: isSaving } = useConfigureEquityStoreTesting();

  const settings = data?.data;
  const groups = settings?.storeGroups ?? [];
  const selectedGroups = groups.filter((group) => selectedIds.includes(group.groupId));
  const allSelected = groups.length > 0 && selectedIds.length === groups.length;
  const isOptOut = chosenPrice === OPT_OUT;

  useEffect(() => {
    if (isError) {
      showToast({
        id: 'equity-store-testing-load-error',
        label: t('error.fetchingFreezeData'),
        toastType: ToastTypes.Error,
      });
    }
  }, [isError, t]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }
  }, [step]);

  const formatPrice = (price: number | null) =>
    price === null || price === undefined ? t('equityStoreTesting.optOut') : `$${Number(price).toFixed(2)}`;

  const yesNo = (value: boolean | null) => (value ? t('common.yes') : t('common.no'));

  const toggleGroup = (groupId: string) => {
    setSelectedIds((current) =>
      current.includes(groupId) ? current.filter((id) => id !== groupId) : [...current, groupId],
    );
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : groups.map((group) => group.groupId));
  };

  const onPriceChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setChosenPrice(value);
    if (value === OPT_OUT) {
      setRedeemableOnline(false);
      setVisibleOnline(false);
    }
  };

  const onRedeemableChange = (checked: boolean) => {
    setRedeemableOnline(checked);
    if (!checked) {
      setVisibleOnline(false);
    }
  };

  const priceOptions = (settings?.priceOptions ?? []).map((price) => ({
    value: Number(price).toFixed(2),
    label: `$${Number(price).toFixed(2)}`,
  }));
  const dropdownOptions = [...priceOptions, { value: OPT_OUT, label: t('equityStoreTesting.optOut') }];

  const confirm = () => {
    if (!settings || !nationalProgramId || !chosenPrice) return;
    const matchedPrice = settings.priceOptions.find((price) => Number(price).toFixed(2) === chosenPrice);
    mutate(
      {
        nationalProgramId,
        body: {
          bundleId: settings.bundleId,
          groupIds: selectedIds,
          nationalChosenPrice: isOptOut ? null : matchedPrice ?? Number(chosenPrice),
          nationalRedeemableOnline: isOptOut ? false : redeemableOnline,
          nationalVisibleOnline: isOptOut || !redeemableOnline ? false : visibleOnline,
        },
      },
      {
        onSuccess: (response) => {
          setResult(response.data);
        },
        onError: () => {
          showToast({
            id: 'equity-store-testing-error',
            label: t('equityStoreTesting.saveError'),
            toastType: ToastTypes.Error,
          });
        },
      },
    );
  };

  const nextDisabled = step === 0 ? selectedIds.length === 0 : step === 1 ? !chosenPrice : isSaving;

  const handleNext = () => {
    if (result) {
      navigate(`/bundles/${bundleId}`);
      return;
    }
    if (step < STEP_COUNT - 1) {
      setStep(step + 1);
      return;
    }
    confirm();
  };

  const groupTable = (
    rows: EquityStoreGroup[],
    selectable: boolean,
    price: string | null,
    redeemable: boolean | null,
    visible: boolean | null,
  ) => (
    <table className={styles.table}>
      <thead>
        <tr>
          {selectable && (
            <th className={styles.checkCell}>
              <Checkbox
                id="equity-select-all"
                label=""
                aria-label={t('equityStoreTesting.selectAll')}
                checked={allSelected}
                onChange={toggleAll}
              />
            </th>
          )}
          <th>{t('equityStoreTesting.storeGroup')}</th>
          <th>{t('equityStoreTesting.selectedPrice')}</th>
          <th>{t('equityStoreTesting.redeemOnline')}</th>
          <th>{t('equityStoreTesting.visibleOnline')}</th>
          <th>{t('equityStoreTesting.orgId')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((group, index) => {
          const selected = selectedIds.includes(group.groupId);
          return (
            <tr
              key={group.groupId}
              className={selected && selectable ? styles.selected : index % 2 === 1 ? styles.alt : undefined}
            >
              {selectable && (
                <td>
                  <Checkbox
                    id={`equity-group-${group.groupId}`}
                    label=""
                    aria-label={t('equityStoreTesting.selectGroup', { name: group.groupName })}
                    checked={selected}
                    onChange={() => toggleGroup(group.groupId)}
                  />
                </td>
              )}
              <td>{group.groupName}</td>
              <td>{price ?? formatPrice(group.nationalProgramPrice)}</td>
              <td>{redeemable === null ? yesNo(group.nationalRedeemableOnline) : yesNo(redeemable)}</td>
              <td>{visible === null ? yesNo(group.nationalVisibleOnline) : yesNo(visible)}</td>
              <td>{orgLabel(group)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const assignments = (
    <Card>
      <h2 className={styles.cardTitle}>{t('equityStoreTesting.assignments')}</h2>
      {groupTable(groups, true, null, null, null)}
      <div className={styles.notice}>
        <Icon iconName="Info" size={20} />
        <span>{t('equityStoreTesting.liveSettingsNotice')}</span>
      </div>
    </Card>
  );

  const newSettings = (
    <div className={styles.stepSection}>
      <h2 className={styles.cardTitle}>{t('equityStoreTesting.assignments')}</h2>
      <h3 className={styles.sectionTitle}>{t('equityStoreTesting.currentSettings')}</h3>
      {groupTable(selectedGroups, false, null, null, null)}
      <Card className={styles.newSettingsCard}>
        <h2 className={styles.cardTitle}>{t('equityStoreTesting.newSettings')}</h2>
        <div className={styles.settingsHeader}>
          <span className={styles.spacer} aria-hidden="true" />
          <span>{t('equityStoreTesting.selectedPrice')}</span>
          <span>{t('equityStoreTesting.redeemOnline')}</span>
          <span>{t('equityStoreTesting.visibleOnline')}</span>
          <span className={styles.spacer} aria-hidden="true" />
        </div>
        <div className={styles.settingsControls}>
          <span className={styles.spacer} aria-hidden="true" />
          <div>
            <Dropdown
              id="equity-price"
              className={styles.priceDropdown}
              label={t('equityStoreTesting.selectedPrice')}
              labelStyle={styles.hiddenLabel}
              placeholderText={t('common.select')}
              value={chosenPrice || 'placeholder'}
              options={dropdownOptions}
              onChange={onPriceChange}
            />
          </div>
          <Switch
            className={styles.settingSwitch}
            color="error"
            size="small"
            checked={!isOptOut && redeemableOnline}
            disabled={isOptOut}
            onChange={(_event, checked) => onRedeemableChange(checked)}
            inputProps={{ 'aria-label': t('equityStoreTesting.redeemOnline') }}
          />
          <Switch
            className={styles.settingSwitch}
            color="error"
            size="small"
            checked={!isOptOut && redeemableOnline && visibleOnline}
            disabled={isOptOut || !redeemableOnline}
            onChange={(_event, checked) => setVisibleOnline(checked)}
            inputProps={{ 'aria-label': t('equityStoreTesting.visibleOnline') }}
          />
          <span className={styles.spacer} aria-hidden="true" />
        </div>
      </Card>
    </div>
  );

  const reviewPrice = isOptOut ? t('equityStoreTesting.optOut') : chosenPrice ? `$${chosenPrice}` : '';
  const reviewRedeemable = isOptOut ? false : redeemableOnline;
  const reviewVisible = isOptOut || !redeemableOnline ? false : visibleOnline;

  const confirmStep = (
    <div className={styles.stepSection}>
      <div className={styles.notice}>
        <Icon iconName="Info" size={20} />
        <span>{t('equityStoreTesting.publishNotice')}</span>
      </div>
      {result ? (
        <EditDrawerSuccessAndFailure apiResponse={result} entityToUpdate={EntityToUpdate.MENU} />
      ) : (
        <Card className={styles.confirmCard}>
          <h2 className={styles.cardTitle}>{t('equityStoreTesting.assignments')}</h2>
          {groupTable(selectedGroups, false, reviewPrice, reviewRedeemable, reviewVisible)}
        </Card>
      )}
    </div>
  );

  const stepContent = [assignments, newSettings, confirmStep][step];
  const primaryLabel = result
    ? t('equityStoreTesting.ok')
    : step === STEP_COUNT - 1
    ? t('common.confirm')
    : t('common.next');

  return (
    <div className={styles.page}>
      <Button
        className={styles.exitButton}
        variant={ButtonVariants.Text}
        onClick={() => navigate(`/bundles/${bundleId}`)}
      >
        {t('common.exit')}
      </Button>
      <div className={styles.heading}>
        <h1 className={styles.title}>{t('equityStoreTesting.title')}</h1>
        <div className={styles.step}>{t('wizard.currentStep', { active: step + 1, total: STEP_COUNT })}</div>
        {settings && (
          <div className={styles.bundleLine}>
            {settings.bundleCode} - {settings.bundleName}
          </div>
        )}
      </div>
      <div ref={bodyRef} className={styles.body}>
        {isLoading ? (
          <div className={styles.loading}>
            <LoadingSpinner />
          </div>
        ) : (
          stepContent
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.back}>
          {step > 0 && !result && (
            <Button variant={ButtonVariants.Secondary} onClick={() => setStep(step - 1)} disabled={isSaving}>
              {t('common.back')}
            </Button>
          )}
        </div>
        <div className={styles.dots} aria-label={t('wizard.currentStep', { active: step + 1, total: STEP_COUNT })}>
          {Array.from({ length: STEP_COUNT }, (_, index) => (
            <span key={index} className={index <= step ? styles.dotActive : styles.dot} />
          ))}
        </div>
        <Button
          className={styles.next}
          onClick={handleNext}
          disabled={nextDisabled && !result}
          isLoading={!result && step === 2 && isSaving}
        >
          {primaryLabel}
        </Button>
      </div>
    </div>
  );
};

export default EquityStoreTesting;
